/**
 * Industry News API
 * Vector similarity ranking for RSS articles against Alert Settings keywords.
 */

import { getAPIClient } from '@tracekit/core';

export interface NewsArticleRankInput {
  id: string;
  title: string;
  description: string;
}

export interface NewsArticleRankResult {
  article_id: string;
  relevance_score: number;
  vector_similarity_score: number;
  keyword_match_score: number;
  matched_keywords: string[];
}

export interface RankNewsArticlesResponse {
  results: NewsArticleRankResult[];
  total_articles: number;
  above_threshold_count: number;
  processing_time_ms: number;
}

export const DEFAULT_NEWS_SIMILARITY_THRESHOLD = 0.3;

/**
 * Backend caps a single rank-articles request at 200 articles (see
 * `RankNewsArticlesRequest.articles` max_length in
 * traceos/api/controllers/rfp/industry_news_ranking.py). Selected RSS feeds regularly
 * return far more than that (RSS_MAX_TOTAL_ARTICLES=750), so without chunking, ranking a
 * full feed page fails outright with a 422 and the whole page silently falls back to
 * unranked date-sort - the root cause of Issue 19 (irrelevant articles shown by default).
 */
export const MAX_ARTICLES_PER_RANK_REQUEST = 200;

const RANK_ARTICLE_PATHS = [
  '/api/industry-news/rank-articles',
  '/api/rfp/industry-news/rank-articles',
];

async function rankArticleChunk(
  projectId: string,
  keywords: string[],
  articles: NewsArticleRankInput[],
  minSimilarityThreshold: number
): Promise<RankNewsArticlesResponse | null> {
  const client = getAPIClient();
  const payload = {
    project_id: projectId,
    keywords,
    articles,
    min_similarity_threshold: minSimilarityThreshold,
  };

  let lastError: unknown;
  for (const path of RANK_ARTICLE_PATHS) {
    try {
      return await client.post<RankNewsArticlesResponse>(path, payload);
    } catch (error) {
      lastError = error;
      const status =
        (error as { status?: number; response?: { status?: number } })?.status ??
        (error as { response?: { status?: number } })?.response?.status;
      if (status !== 404) {
        console.warn('[industryNews] rank-articles failed:', error);
        return null;
      }
    }
  }

  console.warn('[industryNews] rank-articles unavailable on all paths:', lastError);
  return null;
}

function chunkArticles(
  articles: NewsArticleRankInput[],
  size: number
): NewsArticleRankInput[][] {
  if (articles.length <= size) return [articles];
  const chunks: NewsArticleRankInput[][] = [];
  for (let i = 0; i < articles.length; i += size) {
    chunks.push(articles.slice(i, i + size));
  }
  return chunks;
}

export interface RankNewsArticlesProgress {
  completedChunks: number;
  totalChunks: number;
}

export async function rankIndustryNewsArticlesProgressive(
  projectId: string,
  keywords: string[],
  articles: NewsArticleRankInput[],
  minSimilarityThreshold: number = DEFAULT_NEWS_SIMILARITY_THRESHOLD,
  onPartialResults?: (
    results: NewsArticleRankResult[],
    progress: RankNewsArticlesProgress
  ) => void
): Promise<RankNewsArticlesResponse | null> {
  if (articles.length === 0) {
    return { results: [], total_articles: 0, above_threshold_count: 0, processing_time_ms: 0 };
  }

  const chunks = chunkArticles(articles, MAX_ARTICLES_PER_RANK_REQUEST);
  const mergedResults: NewsArticleRankResult[] = [];
  let succeeded = 0;

  for (let index = 0; index < chunks.length; index += 1) {
    const response = await rankArticleChunk(
      projectId,
      keywords,
      chunks[index],
      minSimilarityThreshold
    );
    if (response !== null) {
      mergedResults.push(...response.results);
      succeeded += 1;
      onPartialResults?.(mergedResults, {
        completedChunks: index + 1,
        totalChunks: chunks.length,
      });
    }
  }

  if (succeeded === 0) {
    return null;
  }

  return {
    results: mergedResults,
    total_articles: mergedResults.length,
    above_threshold_count: mergedResults.filter(
      (row) => row.relevance_score >= minSimilarityThreshold
    ).length,
    processing_time_ms: 0,
  };
}

export async function rankIndustryNewsArticles(
  projectId: string,
  keywords: string[],
  articles: NewsArticleRankInput[],
  minSimilarityThreshold: number = DEFAULT_NEWS_SIMILARITY_THRESHOLD
): Promise<RankNewsArticlesResponse | null> {
  if (articles.length === 0) {
    return { results: [], total_articles: 0, above_threshold_count: 0, processing_time_ms: 0 };
  }

  return rankIndustryNewsArticlesProgressive(
    projectId,
    keywords,
    articles,
    minSimilarityThreshold
  );
}
